import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClientOrderService } from '../services';
import { distinctUntilChanged } from 'rxjs';
import { addMonths, addWeeks, format } from 'date-fns';
import * as ibantools from 'ibantools';
import { AlertComponent } from '../components/alert/alert.component';

import { PanelComponent } from '../panel/panel.component';
import { PaymentInterval, PaymentIntervalType } from '../dto';
import { TranslatePipe } from '@ngx-translate/core';

interface DetailsForm {
  companyName: FormControl<string | null>;
  kvkNumber: FormControl<string | null>;
  initials: FormControl<string>;
  firstName: FormControl<string>;
  insertion: FormControl<string | null>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  phoneNumber: FormControl<string>;
  iban: FormControl<string>;
  wishDate: FormControl<string>;
  reseller: FormControl<string | null>;
  paymentInterval: FormControl<PaymentIntervalType>;
}

interface AgreeForm {
  sepa: FormControl<boolean>;
  terms: FormControl<boolean>;
  supplierTerms: FormControl<boolean>;
}

const PATTERN_PHONE = Validators.pattern('(^\\+?)(\\(?\\d+\\)?)([#-.\\s0-9]{9,})');
const PATTERN_ALPHANUMERIC = Validators.pattern('(^[a-zA-Z0-9\\s-.]+$)');

export function IbanValidator(control: AbstractControl): { [key: string]: boolean } | null {
  if (!control.value) return null;
  const isValid = ibantools.isValidIBAN(control.value);
  return isValid ? null : { invalidIBAN: true };
}

@Component({
  selector: 'webmodule-order-panel-details',
  templateUrl: './panel-details.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelComponent, ReactiveFormsModule, AlertComponent, TranslatePipe],
  standalone: true,
})
export class PanelDetailsComponent {
  private readonly orderService = inject(ClientOrderService);
  protected readonly PaymentInterval = PaymentInterval;
  readonly minDate = format(addWeeks(new Date(), 2), 'yyyy-MM-dd');
  readonly maxDate = format(addMonths(new Date(), 6), 'yyyy-MM-dd');
  readonly form = new FormGroup<DetailsForm>({
    initials: new FormControl('', {
      nonNullable: true,
    }),
    firstName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, PATTERN_ALPHANUMERIC],
    }),
    insertion: new FormControl(null),
    lastName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, PATTERN_ALPHANUMERIC],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    iban: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, IbanValidator],
    }),
    phoneNumber: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, PATTERN_PHONE],
    }),
    reseller: new FormControl(null),
    companyName: new FormControl(null),
    kvkNumber: new FormControl(null),
    wishDate: new FormControl(this.minDate, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    paymentInterval: new FormControl(PaymentInterval.monthly, {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  readonly agreeForm = new FormGroup<AgreeForm>({
    sepa: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
    terms: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
    supplierTerms: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
  });
  readonly address = this.orderService.address;
  readonly carrier = this.orderService.deliveryArea();
  readonly pendingRequest = this.orderService.pendingRequest;

  constructor() {
    this.agreeForm.statusChanges.pipe(distinctUntilChanged()).subscribe(() => this.validateDetails());

    this.form.statusChanges.subscribe(() => this.validateDetails());
  }

  private validateDetails() {
    if (this.form.valid && this.agreeForm.valid) {
      this.orderService.updateOrderDetails(
        {
          companyName: this.form.controls.companyName.value ?? '',
          kvkNumber: this.form.controls.kvkNumber.value ?? '',
          firstName: this.form.controls.firstName.value,
          lastName: this.form.controls.lastName.value,
          initials: this.form.controls.initials.value,
          insertion: this.form.controls.insertion.value ?? '',
          email: this.form.controls.email.value,
          address: {
            street: this.address()?.street,
            city: this.address()?.city,
            location: this.orderService.location() ?? Object.create(null),
          },
          iban: this.form.controls.iban.value,
          phoneNumber: this.form.controls.phoneNumber.value,
        },
        this.form.controls.wishDate.value,
        this.form.controls.paymentInterval.value,
        this.form.controls.reseller.value ?? '',
      );
      this.orderService.validOrderDetails.set(true);
    } else {
      console.warn('invalid');
      this.orderService.validOrderDetails.set(false);
    }
  }
}
